/* eslint-disable no-underscore-dangle */
import React, { useContext, useState, useEffect, Fragment } from 'react'
import { usePageView } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import { Separator, LayoutBand } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { usePermission } from '@fs/zion-permissions'
import { useRouteMatch, useLocation } from '@fs/zion-router'
import CardRow from '../card/CardRow'
import Card from '../card/Card'
import { PlaylistContext } from '../PlaylistContext'
import { markGroupsofItemsInPlaylist } from '../../global/playlistHelper'
import api from '../../api/brightspot'
import SpotlightGradient from '../SpotlightGradient'
import LargeLoadingSpinner from '../LargeLoadingSpinner'
import PageBanner from '../PageBanner'
import ContentContainer from '../ContentContainer'
import SectionHeader from '../SectionHeader'
import useIsMobile from '../../hooks/useIsMobile'
import getCreatorsNames from '../../helpers/creatorsNames'
import ChildLimiter from '../ChildLimiter'

const seriesDescriptionStyle = css`
  max-width: 800px;
  font-size: 18px;
  line-height: 28px;
`
const cardRowStyle = css`
  word-break: break-word;
`
const SeriesPage = () => {
  const user = useUser()
  usePageView({ page_detail: 'Guide Me' })
  const { playlist } = useContext(PlaylistContext)

  const [listPromoAreas, setListPromoAreas] = useState()
  const [t] = useTranslation()
  const isMobile = useIsMobile()
  const isMember = usePermission('MemberContentPermission', 'DiscoveryUserContext')
  const { search } = useLocation()
  const routeMatch = useRouteMatch('/series')
  const params = new URLSearchParams(search)
  const previewId = params.get('previewId')
  const pathOrId = previewId || routeMatch?.url

  const { data, loading } = useQuery(api.series.guidemeGQLQuery(pathOrId))
  useEffect(() => {
    if (data?.RootsTechPageEntry?.content) {
      const promoAreas = data.RootsTechPageEntry.content.filter((item) => {
        return item._viewTemplate === '/core/list/List.hbs' // TO DO: Ask for a better identifier back from brightspot
      })

      const playlistMarkedList = markGroupsofItemsInPlaylist({ playlist, items: promoAreas })
      setListPromoAreas(playlistMarkedList)
    }
  }, [data, playlist])

  return (
    <>
      <SpotlightGradient flavor="apple" />
      <PageBanner
        heading={t('series.heading.label', 'Guide Me')}
        subheading={t(
          'series.subheading.label',
          'Not sure where to start? We’ve created collections of popular sessions on various topics so you don’t have to search for everything yourself.'
        )}
      />

      <LayoutBand>
        {loading ? (
          <LargeLoadingSpinner />
        ) : (
          <ContentContainer>
            {listPromoAreas?.map((section, rowIndex) => (
              <Fragment key={section.title}>
                <SectionHeader heading={section.title} small={isMobile} />
                <div css={seriesDescriptionStyle}>{section.description}</div>
                <Separator size={isMobile ? 'sm' : 'lg'} />
                <div css={cardRowStyle}>
                  <ChildLimiter initialCount={4} childWrapper={CardRow}>
                    {section?.items?.map((seriesItem, itemIndex) => (
                      <Fragment key={seriesItem?.contentUUID}>
                        {(!seriesItem?.membersOnly || (seriesItem?.membersOnly && isMember)) && (
                          <Card
                            data-testid={`guideme-row${rowIndex}-card${itemIndex}`}
                            key={seriesItem.contentUUID}
                            image={
                              seriesItem?.image?.custom?.image?.src ??
                              seriesItem?.creators?.[0]?.profilePhoto?.image?.src
                            }
                            primaryText={seriesItem?.title ?? ''}
                            secondaryText={
                              getCreatorsNames(seriesItem?.creators) ?? seriesItem.description ?? seriesItem.detailText
                            }
                            sessionLength={seriesItem.durationText}
                            isLive={seriesItem.isLive}
                            isSeries
                            isInPlaylist={seriesItem.isInPlaylist}
                            contentUUID={seriesItem.contentUUID}
                            isPlaylistable={user?.cisId}
                            url={seriesItem.url}
                          />
                        )}
                      </Fragment>
                    ))}
                  </ChildLimiter>
                </div>
                <Separator size={isMobile ? 'xl' : 'xxl'} />
              </Fragment>
            ))}
          </ContentContainer>
        )}
      </LayoutBand>
    </>
  )
}

export default SeriesPage
